import { ReactNode } from 'react';
import { Button, Input, Modal, Textarea } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { FesConfig, Job } from '../../fes';
import { openDatadogLogs } from './datadog';
import { JobDebugSections } from './JobDebugSections';
import { formatTokenCount, getTotalTokens } from './usage';

const costFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  return String(value);
}

/** Where a job came from, e.g. "schedule (Nightly audit)" or "chat". */
function formatOrigin(origin: Job['origin']): string {
  if (!origin?.channel) {
    return '-';
  }
  if (origin.channel === 'schedule' && origin.scheduleId) {
    return `schedule (${origin.scheduleId})`;
  }
  return origin.channel;
}

function formatReference(id: string | undefined, name?: string): string {
  if (!id) {
    return '-';
  }

  return name ? `${name} (${id})` : id;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

function readText(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

/** What an Azure DevOps dispatch asked for, from the job as listed. */
function AzureDevOpsSection({ job }: { job: Job }) {
  const metadata = job.dispatchMetadata || {};
  const repository = job.context?.repositories?.[0];
  const customProperties =
    metadata.customProperties && typeof metadata.customProperties === 'object' ? metadata.customProperties : undefined;
  const createPullRequest = (customProperties as { createPullRequest?: unknown } | undefined)?.createPullRequest;
  const additionalInstructions = readText(metadata.additionalInstructions)?.trim();

  return (
    <details className="app-skill-array-detail app-job-section" open>
      <summary className="app-skill-array-summary">
        <span className="app-skill-array-summary-title">Azure DevOps</span>
      </summary>
      <div className="app-skill-array-detail-body">
        <dl className="app-job-facts">
          <Fact label="Work item">{formatValue(job.azureResource?.id ?? readText(metadata.workItemId))}</Fact>
          <Fact label="Project">{formatValue(repository?.azureProject)}</Fact>
          <Fact label="Repository">
            {repository?.azureRepositoryUrl ? (
              <a href={repository.azureRepositoryUrl} target="_blank" rel="noopener noreferrer">
                {repository.name || repository.azureRepositoryUrl}
              </a>
            ) : (
              formatValue(repository?.name)
            )}
          </Fact>
          <Fact label="Branch">
            {formatValue(repository?.branchName)}
            {repository?.targetBranch ? ` → ${repository.targetBranch}` : ''}
          </Fact>
          <Fact label="Workflow">{formatValue(readText(metadata.workflowType))}</Fact>
          <Fact label="Reasoning">{formatValue(readText(metadata.reasoning))}</Fact>
          <Fact label="Create pull request">{typeof createPullRequest === 'boolean' ? (createPullRequest ? 'yes' : 'no') : '-'}</Fact>
          {additionalInstructions ? <Fact label="Additional instructions">{additionalInstructions}</Fact> : null}
        </dl>
        {customProperties ? (
          <details className="app-job-step">
            <summary>
              <span className="app-job-step-label">Custom properties</span>
            </summary>
            <pre className="app-chat-tool-output">{JSON.stringify(customProperties, null, 2)}</pre>
          </details>
        ) : null}
      </div>
    </details>
  );
}

/** Model, cost and the branches the run reported pushing. */
function OutcomeSection({ job }: { job: Job }) {
  const results = job.results || [];

  return (
    <details className="app-skill-array-detail app-job-section" open>
      <summary className="app-skill-array-summary">
        <span className="app-skill-array-summary-title">Outcome</span>
      </summary>
      <div className="app-skill-array-detail-body">
        <dl className="app-job-facts">
          <Fact label="Model">{formatValue(job.model)}</Fact>
          <Fact label="Cost">{job.costUsd !== undefined ? costFormatter.format(job.costUsd) : '-'}</Fact>
          <Fact label="Pushed">
            {results.length
              ? results.map(result => {
                  const label = `${result.repository || '-'} · ${result.branch || '-'} · ${result.commitSha?.slice(0, 8) || '-'}`;

                  return (
                    <div key={`${result.repository}-${result.branch}-${result.commitSha}`}>
                      {result.webUrl ? (
                        <a href={result.webUrl} target="_blank" rel="noopener noreferrer">
                          {label}
                        </a>
                      ) : (
                        label
                      )}
                    </div>
                  );
                })
              : 'No pushed branch reported.'}
          </Fact>
        </dl>
      </div>
    </details>
  );
}

export function JobDetailPanel({
  job,
  agentName,
  teamName,
  lookupsLoading,
  lookupError,
  fesConfig,
  tokens,
  onOpenConversation,
  onClose,
}: {
  job: Job | null;
  agentName?: string;
  teamName?: string;
  lookupsLoading: boolean;
  lookupError?: string;
  fesConfig: FesConfig;
  tokens: AuthTokens;
  onOpenConversation?: () => void;
  onClose: () => void;
}) {
  if (!job) {
    return null;
  }

  const agentDisplay = lookupsLoading && job.agentId && !agentName ? 'Loading...' : formatReference(job.agentId, agentName);
  const teamDisplay = lookupsLoading && job.teamId && !teamName ? 'Loading...' : formatReference(job.teamId, teamName);
  const isAzureDevOps = job.origin?.channel === 'azure-devops';

  return (
    <Modal
      buttonCloseLabel="Close job detail"
      buttonConfirmText="Close"
      onClose={onClose}
      onConfirm={onClose}
      shown
      title="Job detail"
    >
      <div className="app-actions app-job-detail-actions">
        {onOpenConversation ? (
          <Button icon="semantic-chat" onClick={onOpenConversation}>
            Open conversation
          </Button>
        ) : null}
        <Button
          secondary
          icon="semantic-forward"
          onClick={() => openDatadogLogs(job.requestId)}
        >
          View Datadog logs
        </Button>
      </div>

      {lookupError ? (
        <div className="app-result app-team-error" role="alert">
          {lookupError}
        </div>
      ) : null}

      <div className="app-detail-grid">
        <label className="app-form-field app-form-field--readonly app-form-field--wide" htmlFor="job-request-id">
          <span>Request ID</span>
          <Input id="job-request-id" value={formatValue(job.requestId)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-installation-id">
          <span>Installation ID</span>
          <Input id="job-installation-id" value={formatValue(job.installationId)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-status">
          <span>Status</span>
          <Input id="job-status" value={formatValue(job.status)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-total-tokens">
          <span>Total Tokens</span>
          <Input id="job-total-tokens" value={formatTokenCount(getTotalTokens(job.usage))} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-input-tokens">
          <span>Input Tokens</span>
          <Input id="job-input-tokens" value={formatTokenCount(job.usage?.inputTokens)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-output-tokens">
          <span>Output Tokens</span>
          <Input id="job-output-tokens" value={formatTokenCount(job.usage?.outputTokens)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-cached-input-tokens">
          <span>Cached Input Tokens</span>
          <Input id="job-cached-input-tokens" value={formatTokenCount(job.usage?.cachedInputTokens)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-reasoning-output-tokens">
          <span>Reasoning Output Tokens</span>
          <Input
            id="job-reasoning-output-tokens"
            value={formatTokenCount(job.usage?.reasoningOutputTokens)}
            readOnly
          />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-email">
          <span>Email</span>
          <Input id="job-email" value={formatValue(job.email)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-created-at">
          <span>Created At</span>
          <Input id="job-created-at" value={formatValue(job.createdAt)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-updated-at">
          <span>Updated At</span>
          <Input id="job-updated-at" value={formatValue(job.updatedAt)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-agent">
          <span>Agent</span>
          <Input id="job-agent" value={agentDisplay} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-team">
          <span>Team</span>
          <Input id="job-team" value={teamDisplay} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly" htmlFor="job-origin">
          <span>Origin</span>
          <Input id="job-origin" value={formatOrigin(job.origin)} readOnly />
        </label>
        <label className="app-form-field app-form-field--readonly app-form-field--wide" htmlFor="job-initial-prompt">
          <span>Initial Prompt</span>
          <Textarea id="job-initial-prompt" value={formatValue(job.initialPrompt)} readOnly rows={5} />
        </label>
      </div>

      <div className="app-job-sections">
        {isAzureDevOps ? <AzureDevOpsSection job={job} /> : null}
        <OutcomeSection job={job} />
        <JobDebugSections
          key={job.requestId}
          fesConfig={fesConfig}
          tokens={tokens}
          requestId={job.requestId}
          defaultOpen={isAzureDevOps}
        />
      </div>
    </Modal>
  );
}
